import { useState, useEffect, useMemo } from 'react';
import { Order } from '../types/orderTypes';
import { APP_CONFIG } from '../config';

const DEFAULT_PAGE_SIZE = 12;
const DEFAULT_PAGE_NUMBER = 1;

/**
 * Calculates pagination for a given set of orders
 * @param orders - Array of orders to paginate
 * @param currentPage - Current page number (0-indexed)
 * @param pageSize - Number of orders per page
 * @returns Pagination result with orders for current page and metadata
 */
const calculatePagination = (
  orders: Order[],
  currentPage: number,
  pageSize: number,
) => {
  const safePageSize = Math.max(pageSize, DEFAULT_PAGE_NUMBER);
  const totalPages = Math.max(
    Math.ceil(orders.length / safePageSize),
    DEFAULT_PAGE_NUMBER,
  );
  const safePage = currentPage % totalPages;
  const paginatedOrders = orders.slice(
    safePage * pageSize,
    (safePage + 1) * pageSize,
  );
  const hasNextPage = safePage < totalPages - DEFAULT_PAGE_NUMBER;

  return {
    orders: paginatedOrders,
    hasNextPage,
    totalPages,
    currentPage: safePage,
  };
};

interface PaginatedOrders {
  orders: Order[];
  hasNextPage: boolean;
  currentPage: number;
  totalPage: number;
}

interface UseDocketPaginationReturn {
  preparing: PaginatedOrders;
  ready: PaginatedOrders;
}

interface PageSizeConfig {
  preparing?: number;
  ready?: number;
}

interface Props {
  preparingOrders: Order[];
  readyOrders: Order[];
  pagination?: PageSizeConfig;
}

/**
 * Custom hook that handles pagination logic for ORB display orders
 *
 * This hook provides pagination functionality for preparing and ready order columns,
 * with independent page management and automatic page rotation for optimal user experience.
 *
 * Key Features:
 * - **Independent Pagination**: Separate page state for preparing and ready boards
 * - **Auto-Rotation**: Automatically advances pages every 10 seconds when multiple pages exist
 * - **Flexible Page Sizes**: Configurable page size per board (defaults to 12 cards)
 * - **Page Wraparound**: Cycles back to first page when reaching the end
 * - **Performance Optimized**: Uses useMemo for pagination calculations to prevent unnecessary re-renders
 *
 * Architecture:
 * - Receives pre-sorted order arrays from useDockets hook
 * - Focuses solely on pagination concerns (single responsibility principle)
 * - Returns paginated results with metadata for UI display
 *
 * Auto-Pagination Logic:
 * - Only advances pages when board has multiple pages (totalPages > 1)
 * - Each board advances independently based on its own page count
 * - Uses configurable PAGINATION_INTERVAL (10 seconds by default)
 * - Automatically cleans up timers on component unmount
 *
 * @param preparingOrders - Pre-sorted array of preparing orders from useDockets
 * @param readyOrders - Pre-sorted array of ready orders from useDockets
 * @param pagination - Optional page size configuration { preparing?: number, ready?: number }
 * @returns Pagination state with orders, metadata, and navigation info for both boards
 *
 * @example
 * ```tsx
 * const { preparing, ready } = useDocketPagination({
 *   preparingOrders,
 *   readyOrders,
 *   pagination: { preparing: 12, ready: 8 }
 * });
 *
 * // Returns: {
 * //   preparing: { orders: Order[], hasNextPage: boolean, currentPage: number, totalPage: number }
 * //   ready: { orders: Order[], hasNextPage: boolean, currentPage: number, totalPage: number }
 * // }
 * ```
 */
export const useDocketPagination = ({
  preparingOrders,
  readyOrders,
  pagination,
}: Props): UseDocketPaginationReturn => {
  const [pageState, setPageState] = useState({ preparing: 0, ready: 0 });

  // Handle different page size configurations
  const preparingPageSize = pagination?.preparing ?? DEFAULT_PAGE_SIZE;
  const readyPageSize = pagination?.ready ?? DEFAULT_PAGE_SIZE;

  // Calculate pagination for both preparing and ready orders using reusable function
  const preparingPagination = useMemo(
    () =>
      calculatePagination(
        preparingOrders,
        pageState.preparing,
        preparingPageSize,
      ),
    [preparingOrders, pageState.preparing, preparingPageSize],
  );

  const readyPagination = useMemo(
    () => calculatePagination(readyOrders, pageState.ready, readyPageSize),
    [readyOrders, pageState.ready, readyPageSize],
  );

  // Auto-pagination timer - advance both boards independently when they have multiple pages
  useEffect(() => {
    if (
      preparingPagination.totalPages > DEFAULT_PAGE_NUMBER ||
      readyPagination.totalPages > DEFAULT_PAGE_NUMBER
    ) {
      const timer = setTimeout(() => {
        setPageState(prev => ({
          // Only advance page if there are multiple pages available
          preparing:
            preparingPagination.totalPages > DEFAULT_PAGE_NUMBER
              ? prev.preparing + DEFAULT_PAGE_NUMBER
              : prev.preparing,
          ready:
            readyPagination.totalPages > DEFAULT_PAGE_NUMBER
              ? prev.ready + DEFAULT_PAGE_NUMBER
              : prev.ready,
        }));
      }, APP_CONFIG.PAGINATION_INTERVAL);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [
    pageState.preparing,
    pageState.ready,
    preparingPagination.totalPages,
    readyPagination.totalPages,
  ]);

  return {
    preparing: {
      orders: preparingPagination.orders,
      hasNextPage: preparingPagination.hasNextPage,
      currentPage: preparingPagination.currentPage + 1,
      totalPage: preparingPagination.totalPages,
    },
    ready: {
      orders: readyPagination.orders,
      hasNextPage: readyPagination.hasNextPage,
      currentPage: readyPagination.currentPage + 1,
      totalPage: readyPagination.totalPages,
    },
  };
};
